<template>
	<div v-if="loading" class="flex justify-center w-full py-5">
		<app-loader size="large" />
	</div>

	<ul v-else-if="journeys.length > 0" role="list" class="mt-2">
		<li
			v-for="(journey, index) in journeys"
			:key="journey.id"
			data-testid="journey"
			class="rounded-lg shadow-sm mb-4 bg-white">
			<router-link
				:to="`/driver-app/journey/${journey.id}/${isComplete(journey) ? 'confirm' : 'details'}`"
				class="p-4 flex justify-between items-center gap-x-6 w-full">
				<div
					class="absolute -left-4 border-slate-200 border-[6px] w-10 h-10 flex items-center justify-center rounded-full bg-white text-mabey-950 font-bold shrink-0 grow-1">
					{{ index + 1 }}
				</div>

				<div class="flex items-center min-w-0 gap-x-4">
					<div class="relative w-12 ms-4 shrink-0">
						<div
							v-if="isComplete(journey)"
							class="flex grow-0 shrink-0 absolute top-0 right-0 z-30 w-4">
							<app-icon
								:icon="['fas', 'fa-check-circle']"
								class="absolute text-green-600 z-20" />
							<app-icon
								:icon="['fas', 'fa-circle']"
								class="absolute text-white z-10" />
						</div>

						<app-svg
							:path="`/icons/catalyst/${
								getStatusIcon(journey, 'journey_status').icon
							}.svg`"
							:colour="getStatusIcon(journey, 'journey_status').colour"
							:class="isComplete(journey) ? 'opacity-40 z-10' : ''"
							autoscale />
					</div>

					<div
						class="min-w-0 flex-auto"
						:class="isComplete(journey) ? 'opacity-40' : ''"
						data-testid="title-body">
						<p
							class="text-sm font-semibold leading-6 text-gray-900"
							data-testid="title">
							{{ journey.link_number }} / {{ journey.journey_number }} /
							{{ Math.round(Number(journey.weight) * 100) / 100 }}t
						</p>
						<p class="mt-1 flex text-xs leading-5 text-gray-500" data-testid="subtitle">
							{{ truncate(journey.delivery_address, { length: 80 }) }}
						</p>
					</div>
				</div>

				<div class="flex shrink-0 items-center gap-x-6">
					<div class="relative flex-none text-xl text-gray-400">
						<app-icon :icon="['fas', 'angle-right']" />
					</div>
				</div>
			</router-link>
		</li>
	</ul>

	<app-alert v-else class="mt-4"> No journey results were found with this type </app-alert>
</template>

<script lang="ts" setup>
	import { ref, onMounted, watch } from 'vue'
	import useAuthStore from '@/stores/auth'
	import useAppStore from '@/stores/app'
	import useToasterStore from '@/stores/toaster'
	import JourneyService from '@/services/journey'
	import type { Journey } from '@/data/schema/journey'
	import { getStatusIcon } from '@/composables/status'
	import AppSvg from '@/components/base/AppSvg.vue'
	import { isComplete } from '@/utils/journeys'
	import { truncate } from 'lodash'

	const api = new JourneyService()
	const auth = useAuthStore()
	const app = useAppStore()
	const toaster = useToasterStore()
	const loading = ref(false)

	const props = withDefaults(
		defineProps<{
			type?: string
		}>(),
		{
			type: undefined
		}
	)

	const journeys = ref<Journey[]>([])

	function getData() {
		loading.value = true

		if (auth.user?.current_depot) {
			api.getDriverAppJourneys(auth.user?.current_depot, {
				journeyType: props.type,
				date: app.queryDate,
				vehicleReg: auth.user.current_vehicle?.registration,
				per_page: 25
			})
				.then((res) => {
					journeys.value = res.data.data
					loading.value = false
				})
				.catch((error) => {
					console.error(error)
					toaster.error({
						text: 'Error occurred getting journey data'
					})
					loading.value = false
				})
		} else {
			toaster.error({
				text: 'Invalid depot value. Please check user account'
			})
		}
	}

	watch(
		() => app.queryDate,
		() => {
			getData()
		}
	)

	watch(
		() => auth.user.current_vehicle,
		() => {
			getData()
		}
	)

	onMounted(() => {
		getData()
	})
</script>
